// ============================================================================ //
// YouTube SMM — MARKETING PRICING (single source of truth for DISPLAY)
// ---------------------------------------------------------------------------- //
// >>> EDIT RETAIL PRICES HERE <<<
//
// These are the retail USD prices shown on the public landing page (/youtube)
// and the dashboard buy page (/dashboard/youtube-smm). Change a number here and
// it updates everywhere in the UI.
//
// IMPORTANT: For the FUNCTIONAL products, the *charged* price comes from the
// backend (`backend/smm_service.py` -> PRODUCT_CONFIGS). All four products
// (Views, Subscribers, Likes, Watch-Hours) are now purchasable (mock mode), so
// keep the numbers below in sync with that backend config.
// ============================================================================ //

export type YtProductId = 'views' | 'subscribers' | 'likes' | 'watch_hours';

export interface YtTier {
  /** Quantity (views / subs / likes) or hours (watch_hours). */
  quantity: number;
  /** Retail price in USD. */
  price: number;
  /** Optional marketing badge, e.g. "Best value", "Popular". */
  badge?: string;
}

export interface YtProduct {
  id: YtProductId;
  /** Display name, e.g. "YouTube Views". */
  name: string;
  /** Short unit label, e.g. "views", "subscribers", "watch hours". */
  unit: string;
  /** One-line marketing tagline. */
  tagline: string;
  /** Whether the product can actually be purchased today. */
  available: boolean;
  /** Package tiers (ascending). */
  tiers: YtTier[];
}

/** Format a tier quantity compactly (1000 -> "1K", 50000 -> "50K"). */
export const formatQty = (n: number): string => {
  if (n >= 1000 && n % 1000 === 0) return `${n / 1000}K`;
  return n.toLocaleString('en-US');
};

/** Format a USD price (4.99 -> "$4.99"). */
export const formatUsd = (n: number): string =>
  `$${n.toFixed(2)}`;

/** Effective price-per-1k for a tier (for "best value" comparisons). */
export const pricePer1k = (t: YtTier): number => t.price / (t.quantity / 1000);

// ---------------------------------------------------------------------------- //
// The four products. All purchasable (mock) — mirror backend PRODUCT_CONFIGS.
// ---------------------------------------------------------------------------- //
export const YOUTUBE_SMM_PRODUCTS: YtProduct[] = [
  {
    id: 'views',
    name: 'YouTube Views',
    unit: 'views',
    tagline: 'Real, high-retention views to boost your video ranking.',
    available: true,
    tiers: [
      { quantity: 1_000, price: 4.99 },
      { quantity: 5_000, price: 19.99, badge: 'Popular' },
      { quantity: 10_000, price: 34.99, badge: 'Best value' },
      { quantity: 25_000, price: 74.99 },
      { quantity: 50_000, price: 129.99 },
    ],
  },
  {
    id: 'subscribers',
    name: 'YouTube Subscribers',
    unit: 'subscribers',
    tagline: 'Grow your channel with genuine-looking subscribers.',
    available: true,
    tiers: [
      { quantity: 100, price: 9.99 },
      { quantity: 250, price: 19.99, badge: 'Popular' },
      { quantity: 500, price: 34.99, badge: 'Best value' },
      { quantity: 1_000, price: 59.99 },
      { quantity: 2_500, price: 129.99 },
    ],
  },
  {
    id: 'likes',
    name: 'YouTube Likes',
    unit: 'likes',
    tagline: 'More likes for stronger social proof and engagement.',
    available: true,
    tiers: [
      { quantity: 500, price: 4.99 },
      { quantity: 1_000, price: 8.99, badge: 'Popular' },
      { quantity: 5_000, price: 34.99, badge: 'Best value' },
      { quantity: 10_000, price: 59.99 },
    ],
  },
  {
    id: 'watch_hours',
    name: 'Watch Hours',
    unit: 'watch hours',
    tagline: 'Hit the 4,000-hour bar for YouTube monetization.',
    available: true,
    tiers: [
      { quantity: 1_000, price: 49.99 },
      { quantity: 2_000, price: 89.99, badge: 'Popular' },
      { quantity: 4_000, price: 169.99, badge: 'Monetization' },
    ],
  },
];

export const getProduct = (id: YtProductId): YtProduct =>
  YOUTUBE_SMM_PRODUCTS.find((p) => p.id === id) as YtProduct;
